import {useState, useMemo, useCallback} from 'react';

import {TreeViewSelection, BasicTreeViewSelectionOptions} from 'filters/features/FilterDialog/types';

import {GenericTreeNode, groupSelectedKeys, getAllLeaveKeys} from 'utils/treeUtils';

import {TreeBasicNode} from 'components/Tree/TreeTypes';

type OptimizedTreeSelectionOptions = BasicTreeViewSelectionOptions & {
  groupSelection?: boolean;
};

export const useOptimizedTreeViewSelection: TreeViewSelection<
  string,
  GenericTreeNode<TreeBasicNode>,
  string[] | null,
  OptimizedTreeSelectionOptions
> = ({treeData}, {value, setValue}, {disabled, groupSelection}) => {
  const [isAllSelected, setIsAllSelected] = useState(false);
  const leaveKeys = useMemo(() => getAllLeaveKeys(treeData), [treeData]);

  return {
    isDisabled: disabled,
    isAllSelected,
    selected: useMemo(() => {
      if (isAllSelected) {
        return treeData.map(node => node.key);
      }

      return value || [];
    }, [isAllSelected, treeData, value]),
    setSelected: useCallback(
      (newSelected: string[]) => {
        const isAllSelected = newSelected.length === leaveKeys.length;
        setIsAllSelected(isAllSelected);

        if (isAllSelected) {
          return setValue(null);
        }

        const selection = groupSelection ? groupSelectedKeys(treeData, newSelected) : new Set(newSelected);

        setValue(selection.size ? Array.from(selection) : null);
      },
      [groupSelection, leaveKeys.length, setValue, treeData]
    ),
    selectionCount: useMemo(() => {
      if (!value?.length) {
        return;
      }

      return value.length;
    }, [value]),
  };
};
